import React, { useState, useEffect } from 'react';
import './Ratio.css';
import Assitance from "./Assitance";
import General from "./General";

const createInitialFormState = () => ({
  windingResistance: {
    '1S1-1S2': { R: { value: '', unit: 'Ω' }, Y: { value: '', unit: 'Ω' }, B: { value: '', unit: 'Ω' } },
    '2S1-2S2': { R: { value: '', unit: 'Ω' }, Y: { value: '', unit: 'Ω' }, B: { value: '', unit: 'Ω' } },
    '3S1-3S2': { R: { value: '', unit: 'Ω' }, Y: { value: '', unit: 'Ω' }, B: { value: '', unit: 'Ω' } },
    '4S1-4S2': { R: { value: '', unit: 'Ω' }, Y: { value: '', unit: 'Ω' }, B: { value: '', unit: 'Ω' } },
    '5S1-5S2': { R: { value: '', unit: 'Ω' }, Y: { value: '', unit: 'Ω' }, B: { value: '', unit: 'Ω' } }
  },
  polarityCheck: {
    '1S1-1S2': { R: { value: '' }, Y: { value: '' }, B: { value: '' } },
    '2S1-2S2': { R: { value: '' }, Y: { value: '' }, B: { value: '' } },
    '3S1-3S2': { R: { value: '' }, Y: { value: '' }, B: { value: '' } },
    '4S1-4S2': { R: { value: '' }, Y: { value: '' }, B: { value: '' } },
    '5S1-5S2': { R: { value: '' }, Y: { value: '' }, B: { value: '' } }
  }
});

const mergeWithInitialState = (incomingState) => {
  const base = createInitialFormState();

  if (!incomingState) {
    return base;
  }

  const sections = ['windingResistance', 'polarityCheck'];
  const phases = ['R', 'Y', 'B'];
  const references = Object.keys(base.windingResistance);

  sections.forEach((section) => {
    references.forEach((ref) => {
      phases.forEach((phase) => {
        const incomingPhaseData = incomingState?.[section]?.[ref]?.[phase];
        if (incomingPhaseData) {
          base[section][ref][phase] = {
            ...base[section][ref][phase],
            ...incomingPhaseData
          };
        }
      });
    });
  });

  return base;
};

const getStoredFormData = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const savedFormData = localStorage.getItem('transformerWindingPolarityData');
    return savedFormData ? JSON.parse(savedFormData) : null;
  } catch (error) {
   
    return null;
  }
};

const RESET_EVENT_NAME = 'ctReportDataCleared';

const TransformerWindingPolarity = ({
  initialData = null,
  selectedCore: selectedCoreProp = null,
  goBackToPolarity,
  goToGeneral
}) => {
  const [selectedCore, setSelectedCore] = useState(() => selectedCoreProp || 1);
  const [currentPage, setCurrentPage] = useState('transformerWinding');
  const [assistanceData, setAssistanceData] = useState(null);
  const [form, setForm] = useState(() => {
    if (initialData) {
      return mergeWithInitialState(initialData);
    }

    const storedForm = getStoredFormData();
    return storedForm ? mergeWithInitialState(storedForm) : createInitialFormState();
  });

  useEffect(() => {
    if (selectedCoreProp) {
      setSelectedCore(parseInt(selectedCoreProp, 10));
    }
  }, [selectedCoreProp]);

  useEffect(() => {
    const loadLocalStorageData = () => {
      try {
        if (!initialData) {
          const savedFormData = localStorage.getItem('transformerWindingPolarityData');
          if (savedFormData) {
            setForm(mergeWithInitialState(JSON.parse(savedFormData)));
          }
        }

        const ctFormData = localStorage.getItem('ctFormData');
        if (ctFormData) {
          const parsedData = JSON.parse(ctFormData);
          if (parsedData.selectedCore) {
            setSelectedCore(parseInt(parsedData.selectedCore, 10));
          }
        }

        const savedAssistanceData = localStorage.getItem('assistanceData');
        if (savedAssistanceData) {
          setAssistanceData(JSON.parse(savedAssistanceData));
        }
      } catch (error) {
        
        setForm(createInitialFormState());
      }
    };

    loadLocalStorageData();
  }, [initialData]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleGlobalReset = () => {
      setForm(createInitialFormState());
      setSelectedCore(selectedCoreProp || 1);
      setCurrentPage('transformerWinding');
      setAssistanceData(null);
      localStorage.removeItem('transformerWindingPolarityData');
      localStorage.removeItem('assistanceData');
    };

    const handleDataReset = () => {
      setForm(createInitialFormState());
      setAssistanceData(null);
      localStorage.removeItem('transformerWindingPolarityData');
      localStorage.removeItem('assistanceData');
    };

    window.addEventListener(RESET_EVENT_NAME, handleGlobalReset);
    window.addEventListener('ctReportDataClearedNoRedirect', handleDataReset);
    return () => {
      window.removeEventListener(RESET_EVENT_NAME, handleGlobalReset);
      window.removeEventListener('ctReportDataClearedNoRedirect', handleDataReset);
    };
  }, [selectedCoreProp]);

  useEffect(() => {
    if (initialData) {
      setForm(mergeWithInitialState(initialData));
    }
  }, [initialData]);

  useEffect(() => {
    const saveToLocalStorage = () => {
      try {
        localStorage.setItem('transformerWindingPolarityData', JSON.stringify(form));
      } catch (error) {
      
      }
    };

    saveToLocalStorage();
  }, [form]);

  const handleBackClick = () => {
    localStorage.setItem('transformerWindingPolarityData', JSON.stringify(form));

    if (typeof goBackToPolarity === 'function') {
      goBackToPolarity(form);
      return;
    }

    setCurrentPage('ctReport');
  };

  const handleNext = () => {
   
    localStorage.setItem('transformerWindingPolarityData', JSON.stringify(form));

    if (typeof goToGeneral === 'function') {
      goToGeneral(form);
      return;
    }

    setCurrentPage('assistance');
  };

  const handleAssistanceDataReturn = (data) => {
    setAssistanceData(data);

    if (data) {
      localStorage.setItem('assistanceData', JSON.stringify(data));
    }

    setCurrentPage('transformerWinding');
  };

  const clearStoredData = () => {
    localStorage.removeItem('transformerWindingPolarityData');
    localStorage.removeItem('assistanceData');

    setForm(createInitialFormState());
    setAssistanceData(null);

    
  };

  const handleWindingResistanceChange = (section, ref, phase, value) => {
    const numericValue = value.replace(/[^0-9.]/g, '');

    setForm(prevForm => {
      const newForm = { ...prevForm };
      newForm[section][ref][phase].value = numericValue;
      return newForm;
    });
  };

  const handlePolarityCheckChange = (section, ref, phase, value) => {
    setForm(prevForm => {
      const newForm = { ...prevForm };
      newForm[section][ref][phase].value = value;
      return newForm;
    });
  };

  const redirectToHome = () => {
    if (window.confirm("Are you sure you want to leave this page")) {
      localStorage.setItem('openReportsOnLoad', 'true');
      window.location.href = "/";
    }
  };

  const handleWindingResistanceUnitChange = (section, ref, phase, unit) => {
    setForm(prevForm => {
      const newForm = { ...prevForm };
      newForm[section][ref][phase].unit = unit;
      return newForm;
    });
  };


  const unitOptions = [
    { value: 'Ω', label: 'Ω' },
    { value: 'mΩ', label: 'mΩ' },
    { value: 'nΩ', label: 'nΩ' }
  ];

  const polarityOptions = [
    { value: '', label: 'Select Polarity' },
    { value: 'Forward', label: 'Forward' },
    { value: 'Reverse', label: 'Reverse' }
  ];

  const renderWindingResistanceSection = () => {
    const references = [];
    for (let i = 1; i <= selectedCore; i++) {
      references.push(`${i}S1-${i}S2`);
    }

    return (
      <div>
        <span
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            fontSize: '20px',
            fontWeight: '600',
            color: '#333',
            cursor: 'pointer',
            marginLeft: '50px',
            marginBottom: '50px'
          }}
          onClick={redirectToHome}
        >
          X
        </span>

        <div className="card">

          <div className="card-header108">
            <h2 className="card-title010">WINDING RESISTANCE</h2>
          </div>
          <div className="table-container00">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>R-ph</th>
                  <th>Y-ph</th>
                  <th>B-ph</th>
                </tr>
              </thead>
              <tbody>
                {references.map((ref) => (
                  <tr key={`windingResistance-${ref}`}>
                    <td className="reference-cell">{ref}</td>
                    {['R', 'Y', 'B'].map((phase) => (
                      <td key={`windingResistance-${ref}-${phase}`} className="input-cell">
                        <div className="input-with-unit">
                          <input
                            className="input-field"
                            placeholder={`Enter ${phase}-ph`}
                            value={form.windingResistance[ref][phase].value}
                            onChange={(e) => handleWindingResistanceChange('windingResistance', ref, phase, e.target.value)}
                          />
                          <select
                            className="unit-dropdown"
                            value={form.windingResistance[ref][phase].unit}
                            onChange={(e) => handleWindingResistanceUnitChange('windingResistance', ref, phase, e.target.value)}
                          >
                            {unitOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="pdf-only-text">
                          {form.windingResistance[ref][phase].value} {form.windingResistance[ref][phase].value ? form.windingResistance[ref][phase].unit : ''}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderPolarityCheckSection = () => {
    const references = [];
    for (let i = 1; i <= selectedCore; i++) {
      references.push(`${i}S1-${i}S2`);
    }

    return (
      <div className="card">
        <div className="card-header108">
          <h2 className="card-title01">POLARITY CHECK</h2>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>R-ph</th>
                <th>Y-ph</th>
                <th>B-ph</th>
              </tr>
            </thead>
            <tbody>
              {references.map((ref) => (
                <tr key={`polarityCheck-${ref}`}>
                  <td className="reference-cell">{ref}</td>
                  {['R', 'Y', 'B'].map((phase) => (
                    <td key={`polarityCheck-${ref}-${phase}`} className="input-cell">
                      <select
                        className="polarity-dropdown"
                        value={form.polarityCheck[ref][phase].value}
                        onChange={(e) => handlePolarityCheckChange('polarityCheck', ref, phase, e.target.value)}
                      >
                        {polarityOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="pdf-only-text">
                        {form.polarityCheck[ref][phase].value}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleReturnFromGeneral = () => {
    setCurrentPage('transformerWinding');
  };

  if (currentPage === 'ctReport') {
    return (
      <General
        selectedCore={selectedCore}
        goBack={() => setCurrentPage('transformerWinding')}
        onReturnToRatio={handleReturnFromGeneral}
      />
    );
  } else if (currentPage === 'assistance') {
    return <Assitance
      selectedCore={selectedCore}
      initialData={assistanceData}
      goBackToPolarity={handleAssistanceDataReturn}
    />;
  }

  return (
    <div className="transformer-container">
      <div className="form-sections">
        {renderWindingResistanceSection()}
        {renderPolarityCheckSection()}
      </div>

      <div className="button-container">
        <button className="btn btn-back" onClick={handleBackClick}>Back</button>
        <button className="btn btn-next" onClick={handleNext}>Next</button>
      </div>
    </div>
  );
};

export default TransformerWindingPolarity;

